import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Trash2, X, AlertCircle, Loader2, Mail, Phone,
  Calendar, Droplets, ChevronDown, ShieldCheck,
  AlertTriangle, MessageSquare, User, CheckCircle2, Home
} from "lucide-react";
import axios from "axios";
import Navigation from "@/components/shared/Navigation";
import Footer from "@/components/shared/Footer";

const BACKEND_URL = process.env.REACT_APP_API_URL;
const API = `${BACKEND_URL}`;

const DeleteAccountFlow = () => {
  const navigate = useNavigate();

  // --- FLOW CONTROL STATE ---
  // Steps: 'phone' -> 'phone-otp' -> 'email-otp' -> 'profile'
  const [currentStep, setCurrentStep] = useState("phone");
  const [activeTab, setActiveTab] = useState("");

  // --- DATA STATE ---
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [userData, setUserData] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token") || "");

  // --- UI STATE ---
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showEmailModal, setShowEmailModal] = useState(false); // From step 1
  const [isSettingsOpen, setIsSettingsOpen] = useState(false); // From profile
  const [showReasonModal, setShowReasonModal] = useState(false); // From profile
  const [showConfirmModal, setShowConfirmModal] = useState(false); // From profile
  const [deleteReason, setDeleteReason] = useState("");
  const [customReason, setCustomReason] = useState("");

  const otpSentRef = useRef(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [currentStep]);

  const scrollToSection = (sectionId) => {
    navigate(`/?section=${sectionId}`);
  };

  // --- STEP 1: PHONE SUBMISSION ---
  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    let normalizedMobile = phone.replace(/\D/g, "");
    if (normalizedMobile.length > 10 && normalizedMobile.startsWith("91")) {
      normalizedMobile = normalizedMobile.slice(-10);
    }

    try {
      const checkResponse = await axios.post(`${API}/user-exists`, { phone: normalizedMobile });
      const { exists, message, email: userEmail } = checkResponse.data;

      if (exists) {
        const otpResponse = await axios.post(`${API}/user/get-otp`, {
          mobile: normalizedMobile,
          app_id: 1,
        });

        if (otpResponse.data.status === "success" || otpResponse.data.message.includes("successfully")) {
          setPhone(normalizedMobile);
          setEmail(userEmail);
          setCurrentStep("phone-otp");
          setOtp(""); // Clear OTP field for next step
        } else {
          throw new Error(otpResponse.data.message || "Failed to send OTP");
        }
      } else {
        if (message === "verify email") setShowEmailModal(true);
        else setError(message || "No account found.");
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  // --- STEP 2: PHONE OTP VERIFICATION ---
  const handlePhoneOtpVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const loginRes = await axios.post(`${API}/user/login`, {
        mobile: phone,
        otp: otp,
        app_id: 2,
        device_id: "web",
        registration_id: "web",
        platform_id: 3,
        user_type_id: 3
      });

      const newToken = loginRes.data.data.access_token;
      setToken(newToken);
      localStorage.setItem("token", newToken);

      // Move to Email OTP step
      setCurrentStep("email-otp");
      setOtp(""); // reset input for email otp
    } catch (err) {
      setError("Invalid OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // --- STEP 3: EMAIL OTP TRIGGER & VERIFY ---
  // Trigger Email OTP when entering the email-otp step
  useEffect(() => {
    if (currentStep === "email-otp" && !otpSentRef.current) {
      const sendEmailOtp = async () => {
        try {
          await axios.post(`${API}/user/update/update-email-otp`,
            { email, force: true },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          otpSentRef.current = true;
        } catch (err) {
          setError(err.response?.data?.message || "Failed to send OTP to email.");
        }
      };
      sendEmailOtp();
    }
  }, [currentStep, email, token]);

  const handleEmailOtpVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await axios.post(`${API}/user/update/update-email-otp`,
        { email, otp: otp, force: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Fetch user profile data before moving to final step
      const profileRes = await axios.get(`${API}/user`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserData(profileRes.data);
      setCurrentStep("profile");
    } catch (err) {
      setError(err.response?.data?.message || "Invalid Email OTP.");
    } finally {
      setLoading(false);
    }
  };

  // --- STEP 4: PROFILE & DELETION ---
  const finalDeleteAccount = async () => {
    setLoading(true);
    try {
      const reasonToSubmit = deleteReason === "Other" ? customReason : deleteReason;
      await axios.delete(`${API}/user/delete-account`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { reason: reasonToSubmit }
      });

      localStorage.clear(); // Clear local data
      setShowConfirmModal(false); // Close the confirmation modal
      setCurrentStep("deleted-success"); // Move to success view
    } catch (err) {
      alert("Failed to delete account. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const reasons = ["Privacy concerns", "No longer need the service", "Found a better alternative", "Too many notifications", "Other"];

  // --- HELPER COMPONENTS ---
  const InfoCard = ({ icon, label, value }) => (
    <div className="bg-white p-6 rounded-2xl border border-gray-100 flex items-start gap-4">
      <div className="p-3 bg-gray-50 rounded-xl text-gray-400">{icon}</div>
      <div>
        <p className="text-xs text-gray-400 uppercase tracking-wider font-bold mb-1">{label}</p>
        <p className="text-[#0F172A] font-semibold">{value || "Not Set"}</p>
      </div>
    </div>
  );

  const ProgressBar = ({ currentStep }) => {
    const steps = ["phone", "phone-otp", "email-otp", "profile"];

    const stepMap = {
      phone: 0,
      "phone-otp": 1,
      "email-otp": 2,
      profile: 3
    };

    const currentIndex = stepMap[currentStep] ?? 0;

    if (currentStep === "deleted-success") return null;

    return (
      <div className="max-w-xl mx-auto mb-8 px-4">
        <div className="flex items-center gap-2">
          {steps.map((_, index) => (
            <div
              key={index}
              className={`flex-1 h-1 rounded-full transition-all duration-300 ${index <= currentIndex
                ? "bg-red-600"
                : "bg-gray-300"
                }`}
            />
          ))}
        </div>
      </div>
    );
  };

  const wordCount = customReason
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#EDF3F4] flex flex-col">
      <Navigation onSectionClick={scrollToSection} activeTab={activeTab} setActiveTab={setActiveTab} />

      <main className="flex-grow flex items-center justify-center py-40 px-6">
        <div className="w-full max-w-4xl mx-auto">
          <ProgressBar currentStep={currentStep} />
          <AnimatePresence mode="wait">
            {/* STEP 1: PHONE INPUT */}
            {currentStep === "phone" && (
              <motion.div key="phone" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="max-w-xl mx-auto w-full">
                <div className="mb-10 text-center">
                  <h1 className="text-[32px] font-bold text-[#0F172A] mb-4">Delete Account</h1>
                  <p className="text-[#5E6574]">Enter your registered phone number to begin.</p>
                </div>
                <div className="bg-white rounded-3xl p-8 md:p-10 shadow-sm border border-gray-100">
                  <form onSubmit={handlePhoneSubmit}>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Registered Phone Number *</label>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+91 98765 43210"
                        required
                        className="w-full px-4 py-3 bg-[#F8F9FA] border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-red-600 focus:ring-2 focus:ring-red-600/10 transition-all"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-4 rounded-xl font-bold text-white flex items-center justify-center gap-2 disabled:opacity-70 text-sm bg-gradient-to-r from-red-500 to-red-600"
                    >
                      {loading ? "Processing..." : <>Proceed </>}
                    </button>
                    {error && <div className="mt-6 text-center p-4 rounded-xl text-sm bg-red-50 text-red-700 border border-red-200">{error}</div>}
                  </form>
                </div>
              </motion.div>
            )}

            {/* STEP 2: PHONE OTP */}
            {currentStep === "phone-otp" && (
              <motion.div key="phone-otp" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="bg-white p-10 rounded-3xl shadow-xl max-w-md w-full mx-auto">
                <h2 className="text-2xl font-bold text-center mb-2">Verify Mobile OTP</h2>
                <p className="text-center text-gray-500 mb-8">Sent to {phone}</p>
                <form onSubmit={handlePhoneOtpVerify} className="space-y-6">
                  <input
                    type="text"
                    maxLength="4"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="Enter OTP"
                    className="w-full text-center text-lg tracking-[0.5em] py-3 bg-gray-50 border rounded-xl outline-none focus:ring-2 focus:ring-red-500"
                    required
                  />
                  {error && <p className="text-red-500 text-sm text-center">{error}</p>}
                  <button disabled={loading} className="w-full py-4 bg-red-600 text-white font-bold rounded-xl transition-all disabled:opacity-50">
                    {loading ? "Verifying..." : "Verify Mobile"}
                  </button>
                  <button type="button" onClick={() => setCurrentStep('phone')} className="w-full text-sm text-gray-500">Back</button>
                </form>
              </motion.div>
            )}

            {/* STEP 3: EMAIL OTP */}
            {currentStep === "email-otp" && (
              <motion.div key="email-otp" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="bg-white p-10 rounded-3xl shadow-xl max-w-md w-full mx-auto">
                <h2 className="text-2xl font-bold text-center mb-2">Verify Email OTP</h2>
                <p className="text-center text-gray-500 mb-8">Sent to {email}</p>
                <form onSubmit={handleEmailOtpVerify} className="space-y-6">
                  <input
                    type="text"
                    maxLength="4"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="4-digit OTP"
                    className="w-full text-center text-lg tracking-widest py-4 bg-gray-50 border rounded-xl outline-none focus:ring-2 focus:ring-red-500"
                    required
                  />
                  {error && <p className="text-red-500 text-sm text-center">{error}</p>}
                  <button disabled={loading} className="w-full py-4 bg-red-600 text-white font-bold rounded-xl transition-all disabled:opacity-50">
                    {loading ? "Verifying..." : "Verify Email"}
                  </button>
                </form>
              </motion.div>
            )}

            {/* STEP 4: PROFILE PAGE */}
            {currentStep === "profile" && userData && (
              <motion.div key="profile" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full">

                {/* PAGE HEADER */}
                <div className="mb-6">
                  <h1 className="text-4xl font-bold text-[#0F172A]">Profile</h1>
                </div>

                {/* PROFILE HEADER */}
                <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100 mb-8 flex flex-col md:flex-row items-center gap-6">
                  <div className="w-24 h-24 bg-red-500/10 rounded-full flex items-center justify-center text-red-500 text-3xl font-bold uppercase">
                    {userData?.first_name?.[0]}{userData?.last_name?.[0]}
                  </div>
                  <div className="text-center md:text-left">
                    <h1 className="text-2xl font-bold text-[#0F172A]">{userData?.first_name} {userData?.last_name}</h1>
                    <p className="text-gray-500 text-sm">Member since {new Date(userData?.created_at).toLocaleDateString()}</p>
                  </div>
                </div>

                {/* DETAILS GRID */}
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <InfoCard icon={<Mail size={18} />} label="Email Address" value={userData?.email} />
                  <InfoCard icon={<Phone size={18} />} label="Mobile Number" value={userData?.mobile} />
                  <InfoCard icon={<Calendar size={18} />} label="Date of Birth" value={userData?.dob} />
                  <InfoCard icon={<Droplets size={18} />} label="Blood Group" value={userData?.blood_group} />
                </div>

                {/* ACCOUNT SETTINGS ACCORDION */}
                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
                  <button onClick={() => setIsSettingsOpen(!isSettingsOpen)} className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center gap-3 text-gray-700 font-semibold">
                      <ShieldCheck size={20} className="text-gray-400" /> Account Settings
                    </div>
                    <ChevronDown size={20} className={`text-gray-400 transition-transform ${isSettingsOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {isSettingsOpen && (
                      <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="px-6 pb-6 overflow-hidden">
                        <div className="pt-4 border-t border-gray-50">
                          <button onClick={() => setShowReasonModal(true)} className="flex items-center gap-2 text-red-500 hover:text-red-600 font-bold text-sm">
                            <Trash2 size={16} />Delete Account
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            )}

            {/* STEP 5: SUCCESS MESSAGE */}
            {currentStep === "deleted-success" && (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="max-w-xl mx-auto w-full text-center"
              >
                <div className="bg-white rounded-3xl p-10 md:p-16 shadow-xl border border-gray-100 flex flex-col items-center">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-8">
                    <CheckCircle2 size={48} className="text-green-600" />
                  </div>

                  <h1 className="text-3xl font-bold text-[#0F172A] mb-4">
                    Account Deletion Scheduled
                  </h1>

                  <div className="space-y-4 mb-10 text-[#5E6574] leading-relaxed">
                    <p>
                      Your account deletion request has been successfully received and processed.
                    </p>
                    <div className="bg-amber-50 p-6 rounded-2xl border border-amber-100 text-amber-800 text-sm">
                      <p className="font-semibold mb-2 flex items-center justify-center gap-2">
                        <AlertCircle size={16} /> 30-Day Recovery Period
                      </p>
                      Your data will be permanently removed from our systems after 30 days, except where retention is required by applicable law. If you change your mind, simply <b>log back into the HealthismPlus App</b> within this period to restore your account.
                    </div>
                  </div>

                  <button
                    onClick={() => navigate("/")}
                    className="w-full py-4 rounded-xl font-bold text-white flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 transition-colors"
                  >
                    <Home size={18} /> Back to Home
                  </button>
                </div>
              </motion.div>)}
          </AnimatePresence>
        </div>
      </main>

      {/* --- MODALS --- */}

      {/* Email Verification Needed Modal (From Step 1) */}
      <AnimatePresence>
        {showEmailModal && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowEmailModal(false)} className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="relative bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4"><AlertCircle size={32} className="text-orange-600" /></div>
              <h3 className="text-xl font-bold mb-2">Verify Your Email</h3>
              <p className="text-[#5E6574] mb-6">Please verify your email from the <b>HealthismPlus App</b> profile section first.</p>
              <button onClick={() => setShowEmailModal(false)} className="w-full py-3 bg-[#006B5A] text-white font-bold rounded-xl">Got it</button>
            </motion.div>
          </div>
        )}

        {/* Reason Modal */}
        {showReasonModal && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-3xl p-8 max-w-2xl w-full relative shadow-xl"
            >
              <button
                onClick={() => setShowReasonModal(false)}
                className="absolute top-5 right-5 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>

              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                  <AlertTriangle size={24} className="text-gray-700" />
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-[#0F172A]">
                    Tell us why you're leaving
                  </h2>
                  <p className="text-gray-500 mt-2">
                    Please share your reason. Your feedback helps us improve.
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-base font-semibold text-[#0F172A] mb-3">
                  Reason for deletion
                </label>

                <textarea
                  rows={8}
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  placeholder="Write at least 50 words about why you're deleting your account..."
                  className="w-full p-4 border border-gray-300 rounded-2xl resize-none outline-none focus:ring-2 focus:ring-red-500"
                />

                <div className="mt-2 text-sm text-gray-500">
                  {wordCount} / 50 words
                </div>
              </div>

              <button
                disabled={wordCount < 50}
                onClick={() => {
                  setDeleteReason(customReason);
                  setShowReasonModal(false);
                  setShowConfirmModal(true);
                }}
                className="w-full mt-6 py-4 bg-red-600 text-white font-bold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            </motion.div>
          </div>
        )}

        {/* Final Confirm Modal */}
        {showConfirmModal && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-3xl p-8 max-w-md w-full text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6 mx-auto"><AlertTriangle size={32} className="text-red-600" /></div>
              <h2 className="text-xl font-bold mb-2">Final Confirmation</h2>
              <p className="text-gray-500 mb-8 text-sm">
                Your account will be deactivated and permanently deleted after 30 days. You can restore it anytime within this period by logging in again.
              </p>
              <div className="flex flex-col gap-3">
                <button disabled={loading} onClick={finalDeleteAccount} className="w-full py-4 bg-red-600 text-white font-bold rounded-xl flex items-center justify-center gap-2">
                  {loading ? <Loader2 className="animate-spin" size={20} /> : "Yes, Delete My Account"}
                </button>
                <button onClick={() => setShowConfirmModal(false)} className="w-full py-4 bg-gray-100 text-gray-700 font-bold rounded-xl">Cancel</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <Footer onSectionClick={scrollToSection} setActiveTab={setActiveTab} />
    </div>
  );
};

export default DeleteAccountFlow;
